<template lang="pug">
  div.br-10
    table.table

      //- TABLE HEADER
      thead
        tr.table__row.table__row--head
          th.table__cell {{ 'base.name' | translate }}
          th.table__cell
            days-count-toggler(v-model="days")
          th.table__cell {{ 'base.rest_days' | translate }}
          th.table__cell {{ 'base.stock_rest' | translate }} [{{ 'base.kg' | translate }}]
          th.table__cell.dry_metter {{ 'base.dry_metter' | translate }} (%)
          th.table__cell {{ 'base.price' | translate }} #[br] {{ 'base.currency' | translate }}
          th.table__cell
      
      //- TABLE BODY
      v-table-body(
        :cols="6"
        :loading="loading"
        :empty="!items.length"
        :label-empty="'info.stock_is_empty' | translate")
        tr.table__row(
          v-for="(ingredient, index) in items"
          :key="ingredient.id")
            td.table__cell {{ ingredient.name }}
            td.table__cell 
              span(:class="{ skeleton: wait }") {{ wait ? '' : Math.floor(ingredient.daily_feed_intake * days) }} 
              span {{ wait ? '' : 'base.kg' | translate }}
            td.table__cell
              span.days-badge(:class="getDaysColor(getRestOfDays(ingredient))") {{ getRestOfDays(ingredient) }}
            td.table__cell {{ ingredient.total_amount | floor }}
            td.table__cell
              .value(v-if="ingredient.name !== 'H2O'")
                input.input-number(
                  :disabled="!hasPermission('ingredient', 'change')"
                  type="number"
                  :min="1"
                  :max="100"
                  @blur="updateDryMatter(ingredient)"
                  name="dry_matter"
                  id="dry_matter"
                  v-model="ingredient.dry_matter"
                )
            td.table__cell.nowrap
              | {{ ingredient.total_price | floor }} {{ 'base.currency' | translate }}
            td.table__cell.table__cell--thin
              v-btn.-mr-1(
                :disabled="!hasPermission('ingredient', 'change')"
                icon
                @click="selected = ingredient")
                v-icon(
                  font-size="1.1"
                  icon="cog")

    //- TABLE PAGINATION
    ui-pagination(
      v-if="showPagination"
      v-model="page"
      :page-count="pageCount"
      @page-change="pageChangeHandler")
    
    //- EDIT MODAL
    v-dialog(
      max-width="340"
      :value="!!selected"
      @input="selected = null")
      edit-modal(
        :data="selected"
        @update="handleUpdate"
        @close="selected = null")
</template>

<script>
import api from '@/api'
import { mapGetters } from 'vuex'
import EditModal from '../modals/EditModal'
import DaysCountToggler from './DaysCountToggler'
import paginationMixin from '@/mixins/pagination.mixin'
import UiNumberInput from '@/components/ui/ui-number-input/UiNumberInput.vue'
import UiPagination from '@/components/ui/ui-pagination/UiPagination'

export default {
  name: 'IngredientsTable',

  mixins: [paginationMixin],

  components: {
    EditModal,
    DaysCountToggler,
    UiPagination,
    UiNumberInput
  },

  data: () => ({
    days: 1,
    selected: null,
    wait: true,
    monitoringSettings: null
  }),

  computed: {
    ...mapGetters('ingredients', [
      'getDailyFeedIntakesData'
    ]),
    dailyFeedIntakesData () {
      return this.getDailyFeedIntakesData
    },
    farmId () {
      const farmId = this.$route.params.farm_id
      return farmId
    },
    allowViewMonitoringFarm () {
      return this.hasPermission('monitoring_farm', 'view')
    }
  },

  async mounted () {
    if (!this.farmId) {
      console.warn('⚠️ Cannot load ingredients: no farm ID')
      return
    }
    try {
      const { data } = await api.monitoring.farms(this.farmId)
      this.monitoringSettings = data
    } catch (error) {
      console.error('Error loading monitoring settings:', error)
    }

    const { query } = this.$route
    this.setupPagination(
      api.ingredients.list,
      {
        farm: this.$route.params.farm_id,
        is_animalfeed: false,
        deleted: false,
        stock: query.stock ? query.stock : '-1',
        page: this.page,
        offset: (this.page - 1) * 100 || null,
        ...query
      }
    )
    this.loadDailyFeedIntakes()
  },

  methods: {
    handleUpdate () {
      const { query } = this.$route
      const stock = query.stock || '-1'
      const page = query.page || '1'
      this.$emit('update', stock, page)
    },

    async updateDryMatter (ingredient) {
      await this.$nextTick()
      ingredient.dry_matter = Math.max(0, Math.min(100, ingredient.dry_matter))

      try {
        const { errors } = await api.ingredients.updateById(ingredient.id, ingredient)
        if (!errors) {
          this.$toasted.show(this.$t('info.changed'))
        }
      } catch (error) {
        this.$toasted.error(this.$t('errors.unexpected'))
      }
    },

    getRestOfDays (item) {
      if (item.daily_feed_intake > 0) {
        return Math.floor(item.total_amount / item.daily_feed_intake)
      }
      return '-'
    },

    getDaysColor (daysLeft) {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      if (daysLeft === '-' || typeof daysLeft !== 'number' || daysLeft < 0) {
        return ''
      }

      if (!this.monitoringSettings || !this.monitoringSettings.daily_intake_left) {
        return ''
      }
      const settings = this.monitoringSettings.daily_intake_left
      if (!settings.enabled) {
        return ''
      }
      if (settings.green !== 0 && daysLeft >= settings.green) {
        return 'days-green'
      }
      if (settings.red !== 0 && daysLeft < settings.red) {
        return 'days-red'
      }
      if (settings.yellow !== 0 && daysLeft < settings.yellow) {
        return 'days-yellow'
      }

      return ''
    },

    async loadDailyFeedIntakes () {
      this.wait = true
      const { query } = this.$route
      try {
        const { data } = await api.ingredients.dailyFeedIntakes({
          farm: this.$route.params.farm_id,
          is_animalfeed: false,
          stock: query.stock ? query.stock : '-1',
          page: this.page,
          offset: (this.page - 1) * 100 || null,
        })
        this.items.forEach(item => {
          const updatedItem = data.find(d => d.id === item.id)
          if (updatedItem) {
            item.daily_feed_intake = updatedItem.daily_feed_intake
          }
        })
        this.wait = false
        this.$forceUpdate()
      } catch (error) {
        console.error('Error loading ingredients:', error)
        this.wait = false
      }
    }
  },

  watch: {
    page () {
      this.loadDailyFeedIntakes()
    },
    dailyFeedIntakesData () {
      this.loadDailyFeedIntakes()
    },
    days () {
      this.loadDailyFeedIntakes()
    }
  }
}
</script>

<style scoped>
.input-number {
  width: auto;
  min-width: 6rem;
  max-width: 9rem;
  background-color: #f8f8f8;
  padding: .8rem;
  border-radius: .5rem;
  font-weight: 500;
  text-align: center;
  border: unset;
}

.input-number:disabled {
  background: unset;
  border: unset;
  padding: 0;
  font-weight: normal;
}

.table__cell.dry_metter {
  min-width: 12rem;
}

span.skeleton {
  width: 2rem;
  display: inline-block;
  height: 2rem;
  background: red;
}

.days-badge {
  display: inline-block;
  padding: 1rem;
  border-radius: 1rem;
  font-weight: 500;
  min-width: 4rem;
  text-align: center;
}

.days-green {
  background-color: #91EA91;
}

.days-yellow {
  background-color: #FBE268;
}

.days-red {
  background-color: #FF9191;
}

.table__row--head th:last-child {
  border-top-right-radius: unset;
    border-bottom-right-radius: unset;
}
#dry_matter:focus {
    background: #ddf2e3;
}
</style>